import { useCallback, useEffect, useRef, useState } from "react";
import api from "../../api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field } from "../FormField";
import { Image, Video, FileText, Upload, Link2, Star, Archive, Pencil, Loader2, ExternalLink } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const CATS = ["Galleria", "Accesso", "Keybox", "Elettrodomestici", "Raccolta differenziata", "Parcheggio", "Lavanderia", "Welcome Guide", "Manuali", "Documenti", "Emergenza", "Altro"];
const VTYPES = ["operativo", "check_in", "check_out", "manutenzione", "tutorial", "emergenza", "elettrodomestici", "raccolta_differenziata", "altro"];
const LANGS = [["", "—"], ["it", "IT"], ["en", "EN"], ["fr", "FR"], ["es", "ES"], ["de", "DE"]];
const KINDS = [["", "Tutti"], ["image", "Foto"], ["video", "Video"], ["document", "Documenti"]];

const KindIcon = ({ k, className }) => k === "video" ? <Video className={className} /> : k === "document" ? <FileText className={className} /> : <Image className={className} />;

export default function MediaCenterTab({ apartmentId, onMainPhotoChange, mainMediaId }) {
  const fileRef = useRef();
  const [items, setItems] = useState([]);
  const [kindFilter, setKindFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const [meta, setMeta] = useState({ category: "Galleria", video_type: "altro", tags: "", description: "", title: "", language: "it" });
  const [ext, setExt] = useState({ external_url: "", category: "Accesso", video_type: "check_in", description: "" });
  const [edit, setEdit] = useState(null);
  const dragIdx = useRef(null);

  const load = useCallback(() => {
    api.get(`/media`, { params: { apartment_id: apartmentId, kind: kindFilter || undefined } })
      .then((r) => setItems(r.data)).catch(() => {});
  }, [apartmentId, kindFilter]);
  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", meta.category); fd.append("video_type", meta.video_type);
      fd.append("tags", meta.tags); fd.append("description", meta.description);
      fd.append("title", meta.title); fd.append("language", meta.language);
      fd.append("apartment_id", apartmentId);
      const r = await api.post(`/media/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(r.data.deduplicated ? "File già in libreria: riutilizzato (nessun duplicato)" : "Media caricato"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore upload"); } finally { setBusy(false); }
  };

  const onDragStart = (i) => { dragIdx.current = i; };
  const onDropCard = async (i) => {
    const from = dragIdx.current; dragIdx.current = null;
    if (from === null || from === i) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(i, 0, moved);
    setItems(next);
    try { await api.post(`/media/reorder`, { ids: next.map((m) => m.id) }); } catch { load(); }
  };
  const addExternal = async () => {
    if (!ext.external_url) return toast.error("Inserisci un URL");
    try { await api.post(`/media/external`, { ...ext, apartment_id: apartmentId }); toast.success("Video esterno collegato"); setExt({ external_url: "", category: "Accesso", video_type: "check_in", description: "" }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const setMain = async (m) => {
    try { await api.post(`/apartments/${apartmentId}/main-photo`, { media_id: m.id }); toast.success("Foto principale aggiornata"); onMainPhotoChange?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const archive = async (m) => {
    try { await api.post(`/media/${m.id}/archive`); toast.success("Archiviato"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const saveEdit = async () => {
    try { await api.put(`/media/${edit.id}`, { category: edit.category, video_type: edit.video_type, description: edit.description, tags: edit.tags, title: edit.title, language: edit.language, expiry_date: edit.expiry_date }); toast.success("Salvato"); setEdit(null); load(); }
    catch { toast.error("Errore"); }
  };

  return (
    <Card className="border-slate-200 p-5" data-testid="detail-media">
      <div className="mb-1 flex items-center justify-between">
        <h3 className="flex items-center gap-2 font-head font-semibold text-slate-900"><Image size={18} /> Media Center <span className="text-sm font-normal text-slate-400">({items.length})</span></h3>
      </div>
      <p className="mb-4 text-xs text-slate-400">Libreria centrale: ogni file esiste una sola volta (dedup automatico) ed è riutilizzabile in tutto Urban OS.</p>

      {/* Upload row */}
      <div className="mb-3 grid grid-cols-2 gap-2 rounded-lg bg-secondary/40 p-3 sm:grid-cols-4" data-testid="media-upload-form">
        <Field label="Categoria">
          <Select value={meta.category} onValueChange={(v) => setMeta((m) => ({ ...m, category: v }))}>
            <SelectTrigger data-testid="media-category"><SelectValue /></SelectTrigger>
            <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Tipo video">
          <Select value={meta.video_type} onValueChange={(v) => setMeta((m) => ({ ...m, video_type: v }))}>
            <SelectTrigger data-testid="media-vtype"><SelectValue /></SelectTrigger>
            <SelectContent>{VTYPES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Tag (virgola)"><Input value={meta.tags} onChange={(e) => setMeta((m) => ({ ...m, tags: e.target.value }))} /></Field>
        <Field label="Titolo"><Input data-testid="media-title" value={meta.title} onChange={(e) => setMeta((m) => ({ ...m, title: e.target.value }))} /></Field>
        <Field label="Lingua">
          <Select value={meta.language} onValueChange={(v) => setMeta((m) => ({ ...m, language: v }))}>
            <SelectTrigger data-testid="media-language"><SelectValue /></SelectTrigger>
            <SelectContent>{LANGS.filter(([v]) => v).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Descrizione" className="sm:col-span-2"><Input value={meta.description} onChange={(e) => setMeta((m) => ({ ...m, description: e.target.value }))} /></Field>
        <div className="sm:col-span-4">
          <input ref={fileRef} type="file" accept="image/*,video/*,application/pdf" className="hidden" data-testid="media-file" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
          <Button size="sm" className="bg-primary hover:bg-primary/90" data-testid="media-upload-btn" disabled={busy} onClick={() => fileRef.current?.click()}>
            {busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Upload size={14} className="mr-1" />}Carica file (foto/video/PDF ≤60MB)
          </Button>
        </div>
      </div>

      {/* External video */}
      <div className="mb-4 flex flex-wrap items-end gap-2 rounded-lg border border-dashed border-slate-200 p-3" data-testid="media-external-form">
        <Field label="Video esterno (YouTube/Drive/Vimeo)"><Input className="w-64" data-testid="media-ext-url" value={ext.external_url} onChange={(e) => setExt((s) => ({ ...s, external_url: e.target.value }))} placeholder="https://..." /></Field>
        <Field label="Categoria">
          <Select value={ext.category} onValueChange={(v) => setExt((s) => ({ ...s, category: v }))}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Button size="sm" variant="outline" data-testid="media-ext-btn" onClick={addExternal}><Link2 size={14} className="mr-1" />Collega</Button>
      </div>

      {/* Filter */}
      <div className="mb-3 flex gap-1.5">
        {KINDS.map(([k, l]) => (
          <button key={k} data-testid={`media-filter-${k || "all"}`} onClick={() => setKindFilter(k)}
            className={`rounded-full px-3 py-1 text-xs font-medium ${kindFilter === k ? "bg-primary text-primary-foreground" : "bg-slate-100 text-slate-600"}`}>{l}</button>
        ))}
      </div>

      {/* Grid */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {items.length === 0 && <p className="col-span-full text-sm text-slate-400">Nessun media. Carica il primo file.</p>}
        {items.map((m, i) => (
          <div key={m.id} data-testid={`media-${m.id}`} draggable
            onDragStart={() => onDragStart(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDropCard(i)}
            className={`cursor-move overflow-hidden rounded-lg border ${mainMediaId === m.id ? "border-amber-400 ring-2 ring-amber-200" : "border-slate-200"}`}>
            <div className="relative flex h-28 items-center justify-center bg-slate-100">
              {mainMediaId === m.id && <span className="absolute left-1.5 top-1.5 z-10 rounded-full bg-amber-500 px-1.5 py-0.5 text-[9px] font-bold text-white">COPERTINA</span>}
              {m.kind === "image" && !m.external ? (
                <img src={`${BACKEND}${m.url}`} alt={m.original_filename} className="h-full w-full object-cover" />
              ) : (
                <a href={m.external ? m.external_url : `${BACKEND}${m.url}`} target="_blank" rel="noreferrer" className="flex flex-col items-center gap-1 text-slate-400">
                  <KindIcon k={m.kind} className="h-8 w-8" />{m.external && <span className="text-[10px]">esterno <ExternalLink size={9} className="inline" /></span>}
                </a>
              )}
            </div>
            <div className="p-2">
              <div className="truncate text-xs font-medium text-slate-700">{m.title || m.original_filename}</div>
              <div className="mb-1.5 flex flex-wrap gap-1">
                <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500">{m.category}</span>
                {m.language && <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] uppercase text-slate-500">{m.language}</span>}
                {m.kind === "video" && m.video_type && <span className="rounded-full bg-indigo-50 px-1.5 py-0.5 text-[10px] text-indigo-600">{m.video_type}</span>}
              </div>
              <div className="flex gap-1">
                {m.kind === "image" && !m.external && <Button size="sm" variant="ghost" className="h-7 px-1.5 text-amber-600" data-testid={`media-main-${m.id}`} title="Imposta come copertina" onClick={() => setMain(m)}><Star size={13} /></Button>}
                <Button size="sm" variant="ghost" className="h-7 px-1.5" data-testid={`media-edit-${m.id}`} onClick={() => setEdit({ ...m, tags: (m.tags || []).join(", "), title: m.title || "", language: m.language || "", expiry_date: m.expiry_date || "" })}><Pencil size={13} /></Button>
                <Button size="sm" variant="ghost" className="h-7 px-1.5 text-slate-500" data-testid={`media-archive-${m.id}`} onClick={() => archive(m)}><Archive size={13} /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent data-testid="media-edit-dialog">
          <DialogHeader><DialogTitle>Modifica media</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-3">
              <Field label="Titolo"><Input data-testid="media-edit-title" value={edit.title} onChange={(e) => setEdit((s) => ({ ...s, title: e.target.value }))} /></Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Categoria">
                  <Select value={edit.category} onValueChange={(v) => setEdit((s) => ({ ...s, category: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Lingua">
                  <Select value={edit.language || ""} onValueChange={(v) => setEdit((s) => ({ ...s, language: v }))}>
                    <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>{LANGS.filter(([v]) => v).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
              </div>
              {edit.kind === "video" && (
                <Field label="Tipo video">
                  <Select value={edit.video_type} onValueChange={(v) => setEdit((s) => ({ ...s, video_type: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{VTYPES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
              )}
              {edit.kind === "document" && <Field label="Scadenza (opz.)"><Input type="date" value={edit.expiry_date || ""} onChange={(e) => setEdit((s) => ({ ...s, expiry_date: e.target.value }))} /></Field>}
              <Field label="Tag (virgola)"><Input value={edit.tags} onChange={(e) => setEdit((s) => ({ ...s, tags: e.target.value }))} /></Field>
              <Field label="Descrizione"><Textarea value={edit.description} onChange={(e) => setEdit((s) => ({ ...s, description: e.target.value }))} /></Field>
              <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setEdit(null)}>Annulla</Button><Button className="bg-primary hover:bg-primary/90" data-testid="media-edit-save" onClick={saveEdit}>Salva</Button></div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
